/**
 * Unified Bet Context
 *
 * Combines BetBuilderContext and BetTrackerContext into a single provider
 * to reduce context nesting and improve performance.
 *
 * This context handles:
 * - Current bet slip (builder)
 * - Saved builders (persisted configurations)
 * - Tracked bets (placed bets with P/L tracking)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  getSavedBuildersWithStatus,
  createSavedBuilder,
  deleteSavedBuilder,
  getTrackedBets,
  createTrackedBet,
  settleTrackedBet,
  deleteTrackedBet,
  waitForAuthReady,
  type SavedBuilderAPI,
  type SavedBuilderWithStatusAPI,
  type TrackedBetAPI,
  type TrackedBetCreatePayload,
} from '../api'

// ============================================
// TYPES
// ============================================

export interface SavedBet {
  id: string
  fixtureId: number
  fixture: string
  title: string
  line?: number
  marketType: 'corners' | 'shots' | 'shots_on_target' | 'cards' | 'goals' | 'other'
  scope: 'team' | 'player' | 'match'
  teamType: 'home' | 'away' | 'total'
  isOver: boolean
  confidence: 'low' | 'med' | 'high'
  confidencePct: number
  odds?: number
}

export interface InvalidatedPlayer {
  playerId: number
  playerName: string
  selectionTitle: string
  status: string
  reason: string | null
}

export interface SavedBuilderItem {
  id: number
  fixtureId: number
  name: string | null
  selections: SavedBet[]
  createdAt: string
  updatedAt: string
  isInvalid: boolean
  invalidatedPlayers: InvalidatedPlayer[]
}

export interface BetSelection extends SavedBet {}

export interface TrackedBetSlip {
  id: string
  backendId?: number
  selections: BetSelection[]
  oddsDecimal: number | null
  stake: number | null
  status: 'pending' | 'won' | 'lost'
  placedAt: string
  settledAt?: string
}

// ============================================
// CONTEXT TYPE
// ============================================

interface BetContextType {
  // Builder state
  bets: SavedBet[]
  addBet: (bet: SavedBet) => void
  removeBet: (id: string) => void
  clearAll: () => void
  isBetSaved: (id: string) => boolean

  // Modal state
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void

  // Saved builders
  savedBuilders: SavedBuilderItem[]
  isSavingBuilder: boolean
  isLoadingSavedBuilders: boolean
  saveCurrentBuilder: (name?: string) => Promise<void>
  loadSavedBuilder: (builder: SavedBuilderItem) => void
  deleteSavedBuilderById: (id: number) => Promise<void>
  refreshSavedBuilders: () => Promise<void>

  // Tracked bets
  trackedBets: TrackedBetSlip[]
  isLoadingTrackedBets: boolean
  isSyncingTrackedBets: boolean
  addTrackedBet: (bet: TrackedBetSlip) => void
  updateTrackedBet: (id: string, updates: Partial<TrackedBetSlip>) => void
  removeTrackedBet: (id: string) => void
  settleTrackedBet: (id: string, status: 'won' | 'lost') => void

  // Computed values
  getPendingCount: () => number
  getTotalStaked: () => number
  getPotentialReturns: () => number
  getProfitLoss: () => number
}

const BetContext = createContext<BetContextType | null>(null)

// ============================================
// HELPERS
// ============================================

const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

const BETS_STORAGE_KEY = 'statscout_bets'
const TRACKED_STORAGE_KEY = 'statscout_tracked_bets'

function apiToSavedBuilder(api: SavedBuilderWithStatusAPI): SavedBuilderItem {
  return {
    id: api.id,
    fixtureId: api.fixture_id,
    name: api.name,
    selections: JSON.parse(api.selections_json),
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    isInvalid: api.is_invalid,
    invalidatedPlayers: api.invalidated_players.map((p) => ({
      playerId: p.player_id,
      playerName: p.player_name,
      selectionTitle: p.selection_title,
      status: p.status,
      reason: p.reason,
    })),
  }
}

function apiToNewSavedBuilder(api: SavedBuilderAPI): SavedBuilderItem {
  return {
    id: api.id,
    fixtureId: api.fixture_id,
    name: api.name,
    selections: JSON.parse(api.selections_json),
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    isInvalid: false,
    invalidatedPlayers: [],
  }
}

function apiToTrackedBet(bet: TrackedBetAPI): TrackedBetSlip {
  return {
    id: `backend-${bet.id}`,
    backendId: bet.id,
    selections: bet.selections.map((sel) => ({
      id: `${bet.id}-${sel.id}`,
      fixtureId: sel.fixture_id || 0,
      fixture: sel.fixture_name,
      title: sel.title,
      line: sel.line,
      marketType: sel.market_type as any,
      scope: sel.scope,
      teamType: sel.team_type,
      isOver: sel.is_over,
      confidence: (sel.confidence || 'med') as any,
      confidencePct: sel.confidence_pct || 50,
      odds: sel.odds,
    })),
    oddsDecimal: bet.odds_decimal,
    stake: bet.stake,
    status: bet.status === 'void' || bet.status === 'cashout' ? 'lost' : bet.status,
    placedAt: bet.placed_at,
    settledAt: bet.settled_at || undefined,
  }
}

function trackedBetToApi(bet: TrackedBetSlip): TrackedBetCreatePayload {
  return {
    selections: bet.selections.map((sel) => ({
      fixture_id: sel.fixtureId,
      fixture_name: sel.fixture,
      title: sel.title,
      line: sel.line,
      market_type: sel.marketType,
      scope: sel.scope,
      team_type: sel.teamType,
      is_over: sel.isOver,
      confidence: sel.confidence,
      confidence_pct: sel.confidencePct,
      odds: sel.odds,
    })),
    odds_decimal: bet.oddsDecimal || undefined,
    stake: bet.stake || undefined,
    placed_at: bet.placedAt,
  }
}

// ============================================
// PROVIDER
// ============================================

export function BetProvider({ children }: { children: React.ReactNode }) {
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false, isLoaded: true }
  const { isSignedIn, isLoaded } = auth

  // ---- Builder State ----
  const [bets, setBets] = useState<SavedBet[]>(() => {
    const stored = localStorage.getItem(BETS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [savedBuilders, setSavedBuilders] = useState<SavedBuilderItem[]>([])
  const [isSavingBuilder, setIsSavingBuilder] = useState(false)
  const [isLoadingSavedBuilders, setIsLoadingSavedBuilders] = useState(false)

  // ---- Tracked Bets State ----
  const [trackedBets, setTrackedBets] = useState<TrackedBetSlip[]>(() => {
    const stored = localStorage.getItem(TRACKED_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  })
  const [isLoadingTrackedBets, setIsLoadingTrackedBets] = useState(false)
  const [isSyncingTrackedBets, setIsSyncingTrackedBets] = useState(false)
  const hasFetchedTrackedBets = useRef(false)

  // ---- Persist to localStorage ----
  useEffect(() => {
    localStorage.setItem(BETS_STORAGE_KEY, JSON.stringify(bets))
  }, [bets])

  useEffect(() => {
    localStorage.setItem(TRACKED_STORAGE_KEY, JSON.stringify(trackedBets))
  }, [trackedBets])

  // ---- Fetch saved builders on sign in ----
  useEffect(() => {
    if (!clerkEnabled || !isLoaded || !isSignedIn) {
      setSavedBuilders([])
      return
    }

    const fetchBuilders = async () => {
      await waitForAuthReady()
      setIsLoadingSavedBuilders(true)
      try {
        const builders = await getSavedBuildersWithStatus()
        setSavedBuilders(builders.map(apiToSavedBuilder))
      } catch (err) {
        console.error('Failed to fetch saved builders:', err)
      } finally {
        setIsLoadingSavedBuilders(false)
      }
    }

    let cancelled = false
    fetchBuilders().then(() => {
      if (cancelled) return
    })
    return () => {
      cancelled = true
    }
  }, [isSignedIn, isLoaded])

  // ---- Fetch tracked bets on sign in ----
  useEffect(() => {
    if (!clerkEnabled || !isLoaded) return
    if (!isSignedIn) {
      hasFetchedTrackedBets.current = false
      return
    }
    if (hasFetchedTrackedBets.current) return

    const fetchBets = async () => {
      await waitForAuthReady()
      setIsLoadingTrackedBets(true)
      try {
        const backendBets = await getTrackedBets()
        const frontendBets = backendBets.map(apiToTrackedBet)

        // Merge with local bets
        const localOnlyBets = trackedBets.filter((b) => !b.backendId)

        if (localOnlyBets.length > 0) {
          setIsSyncingTrackedBets(true)
          for (const localBet of localOnlyBets) {
            try {
              const created = await createTrackedBet(trackedBetToApi(localBet))
              frontendBets.push(apiToTrackedBet(created))
            } catch (err) {
              console.error('Failed to sync local bet:', err)
              frontendBets.push(localBet)
            }
          }
          setIsSyncingTrackedBets(false)
        }

        setTrackedBets(frontendBets)
        hasFetchedTrackedBets.current = true
      } catch (err) {
        console.error('Failed to fetch tracked bets:', err)
      } finally {
        setIsLoadingTrackedBets(false)
      }
    }

    let cancelled = false
    fetchBets().then(() => {
      if (cancelled) return
    })
    return () => {
      cancelled = true
    }
  }, [isSignedIn, isLoaded])

  // ---- Builder Methods ----
  const addBet = useCallback((bet: SavedBet) => {
    setBets((prev) => {
      if (prev.some((b) => b.id === bet.id)) return prev
      return [...prev, bet]
    })
  }, [])

  const removeBet = useCallback((id: string) => {
    setBets((prev) => prev.filter((b) => b.id !== id))
  }, [])

  const clearAll = useCallback(() => {
    setBets([])
  }, [])

  const isBetSaved = useCallback((id: string) => bets.some((b) => b.id === id), [bets])

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const saveCurrentBuilder = useCallback(
    async (name?: string) => {
      if (!clerkEnabled || !isSignedIn || bets.length === 0) return

      const fixtureId = bets[0].fixtureId
      const selectionsJson = JSON.stringify(bets)

      setIsSavingBuilder(true)
      try {
        const created = await createSavedBuilder(fixtureId, selectionsJson, name)
        setSavedBuilders((prev) => [apiToNewSavedBuilder(created), ...prev])
      } catch (err) {
        console.error('Failed to save builder:', err)
        throw err
      } finally {
        setIsSavingBuilder(false)
      }
    },
    [isSignedIn, bets]
  )

  const loadSavedBuilder = useCallback((builder: SavedBuilderItem) => {
    setBets(builder.selections)
  }, [])

  const deleteSavedBuilderById = useCallback(
    async (id: number) => {
      if (!clerkEnabled || !isSignedIn) return

      try {
        await deleteSavedBuilder(id)
        setSavedBuilders((prev) => prev.filter((b) => b.id !== id))
      } catch (err) {
        console.error('Failed to delete saved builder:', err)
        throw err
      }
    },
    [isSignedIn]
  )

  const refreshSavedBuilders = useCallback(async () => {
    if (!clerkEnabled || !isSignedIn) return

    setIsLoadingSavedBuilders(true)
    try {
      const builders = await getSavedBuildersWithStatus()
      setSavedBuilders(builders.map(apiToSavedBuilder))
    } catch (err) {
      console.error('Failed to refresh saved builders:', err)
    } finally {
      setIsLoadingSavedBuilders(false)
    }
  }, [isSignedIn])

  // ---- Tracked Bets Methods ----
  const addTrackedBetFn = useCallback(
    async (bet: TrackedBetSlip) => {
      setTrackedBets((prev) => {
        if (prev.some((b) => b.id === bet.id)) return prev
        return [...prev, bet]
      })

      if (clerkEnabled && isSignedIn) {
        try {
          const created = await createTrackedBet(trackedBetToApi(bet))
          setTrackedBets((prev) => prev.map((b) => (b.id === bet.id ? apiToTrackedBet(created) : b)))
        } catch (err) {
          console.error('Failed to create bet on backend:', err)
        }
      }
    },
    [isSignedIn]
  )

  const updateTrackedBet = useCallback((id: string, updates: Partial<TrackedBetSlip>) => {
    setTrackedBets((prev) => prev.map((bet) => (bet.id === id ? { ...bet, ...updates } : bet)))
  }, [])

  const removeTrackedBet = useCallback(
    async (id: string) => {
      const bet = trackedBets.find((b) => b.id === id)
      setTrackedBets((prev) => prev.filter((b) => b.id !== id))

      if (clerkEnabled && isSignedIn && bet?.backendId) {
        try {
          await deleteTrackedBet(bet.backendId)
        } catch (err) {
          console.error('Failed to delete bet from backend:', err)
        }
      }
    },
    [isSignedIn, trackedBets]
  )

  const settleTrackedBetFn = useCallback(
    async (id: string, status: 'won' | 'lost') => {
      const bet = trackedBets.find((b) => b.id === id)

      setTrackedBets((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status, settledAt: new Date().toISOString() } : b))
      )

      if (clerkEnabled && isSignedIn && bet?.backendId) {
        try {
          await settleTrackedBet(bet.backendId, status)
        } catch (err) {
          console.error('Failed to settle bet on backend:', err)
        }
      }
    },
    [isSignedIn, trackedBets]
  )

  // ---- Computed Values ----
  const getPendingCount = useCallback(() => {
    return trackedBets.filter((b) => b.status === 'pending').length
  }, [trackedBets])

  const getTotalStaked = useCallback(() => {
    return trackedBets.reduce((sum, bet) => sum + (bet.stake || 0), 0)
  }, [trackedBets])

  const getPotentialReturns = useCallback(() => {
    return trackedBets
      .filter((b) => b.status === 'pending')
      .reduce((sum, bet) => {
        if (bet.stake && bet.oddsDecimal) {
          return sum + bet.stake * bet.oddsDecimal
        }
        return sum
      }, 0)
  }, [trackedBets])

  const getProfitLoss = useCallback(() => {
    return trackedBets.reduce((sum, bet) => {
      if (!bet.stake) return sum
      if (bet.status === 'won' && bet.oddsDecimal) {
        return sum + bet.stake * (bet.oddsDecimal - 1)
      }
      if (bet.status === 'lost') {
        return sum - bet.stake
      }
      return sum
    }, 0)
  }, [trackedBets])

  // ---- Memoized value ----
  const value = useMemo(
    () => ({
      // Builder
      bets,
      addBet,
      removeBet,
      clearAll,
      isBetSaved,
      isModalOpen,
      openModal,
      closeModal,
      savedBuilders,
      isSavingBuilder,
      isLoadingSavedBuilders,
      saveCurrentBuilder,
      loadSavedBuilder,
      deleteSavedBuilderById,
      refreshSavedBuilders,
      // Tracked
      trackedBets,
      isLoadingTrackedBets,
      isSyncingTrackedBets,
      addTrackedBet: addTrackedBetFn,
      updateTrackedBet,
      removeTrackedBet,
      settleTrackedBet: settleTrackedBetFn,
      // Computed
      getPendingCount,
      getTotalStaked,
      getPotentialReturns,
      getProfitLoss,
    }),
    [
      bets,
      addBet,
      removeBet,
      clearAll,
      isBetSaved,
      isModalOpen,
      openModal,
      closeModal,
      savedBuilders,
      isSavingBuilder,
      isLoadingSavedBuilders,
      saveCurrentBuilder,
      loadSavedBuilder,
      deleteSavedBuilderById,
      refreshSavedBuilders,
      trackedBets,
      isLoadingTrackedBets,
      isSyncingTrackedBets,
      addTrackedBetFn,
      updateTrackedBet,
      removeTrackedBet,
      settleTrackedBetFn,
      getPendingCount,
      getTotalStaked,
      getPotentialReturns,
      getProfitLoss,
    ]
  )

  return <BetContext.Provider value={value}>{children}</BetContext.Provider>
}

// ============================================
// HOOKS
// ============================================

export function useBet() {
  const ctx = useContext(BetContext)
  if (!ctx) throw new Error('useBet must be used within BetProvider')
  return ctx
}

// Convenience hooks for backward compatibility
export function useBetBuilder() {
  const ctx = useBet()
  return {
    bets: ctx.bets,
    addBet: ctx.addBet,
    removeBet: ctx.removeBet,
    clearAll: ctx.clearAll,
    isBetSaved: ctx.isBetSaved,
    isModalOpen: ctx.isModalOpen,
    openModal: ctx.openModal,
    closeModal: ctx.closeModal,
    savedBuilders: ctx.savedBuilders,
    isSaving: ctx.isSavingBuilder,
    isLoadingSaved: ctx.isLoadingSavedBuilders,
    saveCurrentBuilder: ctx.saveCurrentBuilder,
    loadSavedBuilder: ctx.loadSavedBuilder,
    deleteSavedBuilderById: ctx.deleteSavedBuilderById,
    refreshSavedBuilders: ctx.refreshSavedBuilders,
  }
}

export function useBetTracker() {
  const ctx = useBet()
  return {
    trackedBets: ctx.trackedBets,
    addTrackedBet: ctx.addTrackedBet,
    updateBet: ctx.updateTrackedBet,
    removeBet: ctx.removeTrackedBet,
    settleBet: ctx.settleTrackedBet,
    getPendingCount: ctx.getPendingCount,
    getTotalStaked: ctx.getTotalStaked,
    getPotentialReturns: ctx.getPotentialReturns,
    getProfitLoss: ctx.getProfitLoss,
    isLoading: ctx.isLoadingTrackedBets,
    isSyncing: ctx.isSyncingTrackedBets,
  }
}

// Types are already exported above via interface/type declarations
